import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  Inventory as InventoryIcon,
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { formatIdentification, formatNumberWithCommas } from '../../../utils/generalFunctions';
import { fetchClientes } from '../../../utils/generalsFetch';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';

const RECOVERY_REASONS = [
  { id: "impago", name: "Impago de cuotas" },
  { id: "vencimiento", name: "Vencimiento de plazo" },
  { id: "incumplimiento", name: "Incumplimiento de condiciones" },
  { id: "fraude", name: "Fraude o falsificación" },
  { id: "voluntario", name: "Recuperación voluntaria" },
  { id: "otro", name: "Otro motivo" }
];

const RECOVERY_PRIORITIES = [
  { id: "baja", name: "Baja", color: "success" },
  { id: "media", name: "Media", color: "warning" },
  { id: "alta", name: "Alta", color: "error" },
  { id: "urgente", name: "Urgente", color: "error" }
];

export default function CrearRecuperacionDialog({ open, onClose, onConfirm, prestamo = null, solicitudesActivas = [] }) {
  const [formData, setFormData] = useState({
    cliente: null,
    producto: null,
    prestamo_id: "",
    motivo: "",
    prioridad: "media",
    descripcion: "",
    ubicacion_actual: "",
    contacto_alternativo: "",
    observaciones: "",
    monto_recuperacion: ""
  });

  const [clientes, setClientes] = useState([]);
  const [prestamos, setPrestamos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [clienteInputValue, setClienteInputValue] = useState("");
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertTitle, setAlertTitle] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  useEffect(() => {
    if (open) {
        const fetchLoans = async () => {
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/prestamos/activos/${localStorage.getItem('idcompany')}`,{
                headers: {
                    'Content-Type': 'application/json',
                    'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
                }
            });
            const data = await response.json();
            setPrestamos(data.filter(prestamo => prestamo.producto != "" && !solicitudesActivas.some(solicitud => solicitud.producto.nombre === prestamo.producto)));
        };
        fetchLoans();        
    }
  }, [open,solicitudesActivas]);

  useEffect(() => {
    if (prestamo) {
      setFormData(prev => ({
        ...prev,
        prestamo_id: prestamo.prestamo_original_id || "",
        cliente: {
          id: prestamo.cliente_id,
          nombre: prestamo.cliente_nombre,
          identificacion: prestamo.cliente_identificacion
        }
      }));
    }
  }, [prestamo]);



  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.prestamo) {
      newErrors.prestamo = "Producto es requerido";
    }
    
    if (!formData.motivo) {
      newErrors.motivo = "Motivo de recuperación es requerido";
    }

    if (!formData.monto_recuperacion || formData.monto_recuperacion <= 0 || isNaN(formData.monto_recuperacion)) {
      newErrors.monto_recuperacion = "El monto de la recuperación debe ser mayor a 0";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        prestamo_id: formData.prestamo.prestamo_original_id,
        cliente_id: formData.prestamo.id_cliente,
        motivo: formData.motivo,
        prioridad: formData.prioridad,
        monto_recuperacion: parseFloat(formData.monto_recuperacion.replace(/,/g, "")),
        descripcion: formData.descripcion,
        ubicacion_actual: formData.ubicacion_actual,
        contacto_alternativo: formData.contacto_alternativo,
        observaciones: formData.observaciones,
        id_sucursal: localStorage.getItem('id_sucursal'),
        created_by: localStorage.getItem('id'),
        idcompany: localStorage.getItem('idcompany')
      };

      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/recuperacion/solicitud`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': localStorage.getItem('idcompany')
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Error al crear la solicitud de recuperación');
      }

      const data = await response.json();
      
      // Show success alert
      setAlertTitle("Éxito");
      setAlertMessage("Solicitud de recuperación creada correctamente");
      setAlertSeverity("success");
      setAlertOpen(true);
      
      // Call onConfirm with the new data
      onConfirm(data);
      
    } catch (error) {
      console.error('Error creating recovery request:', error);
      
      // Show error alert
      setAlertTitle("Error");
      setAlertMessage(error.message || "Error al crear la solicitud de recuperación");
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
    // If it was a success, close the dialog
    if (alertSeverity === "success") {
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      cliente: null,
      prestamo: null,
      prestamo_id: "",
      motivo: "",
      prioridad: "media",
      descripcion: "",
      ubicacion_actual: "",
      contacto_alternativo: "",
      observaciones: "",
      monto_recuperacion: ""
    });
    setErrors({});
    setClienteInputValue("");
    setProductos([]);
    onClose();
  };

  const getPriorityColor = (priority) => {
    const priorityObj = RECOVERY_PRIORITIES.find(p => p.id === priority);
    return priorityObj ? priorityObj.color : "default";
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight={600}>
            Solicitud de Recuperación
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>

            {/* Producto Section */}
            <Grid item size={12} mt={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InventoryIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Producto a Recuperar
                </Typography>
              </Box>
            </Grid>


            <Grid item size={12} mt={-2}>
              <Typography variant="body1" fontWeight={600} ml={1}>A Recuperar:</Typography>
              <Autocomplete
                options={prestamos}
                getOptionLabel={(option) => option.loan_id + " - " + option.cliente_nombre + " - " + JSON.parse(option.cliente_campos)["2"] + " - " + option.producto}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    placeholder="Puede buscar por nombre o ID"
                    error={!!errors.prestamo}
                    helperText={errors.prestamo}
                    required
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Typography variant="body1" >{option.loan_id} - {option.cliente_nombre} - {JSON.parse(option.cliente_campos)["2"]}</Typography>
                    <Chip label={option.producto} size="small" color="primary" sx={{ml:1}}/>
                  </li>
                )}
                onChange={(e, val) => setFormData(prev => ({ ...prev, prestamo: val }))}
                value={formData.prestamo}
              />
            </Grid>



            {/* Cliente Section */}
            <Grid item size={12}>
            <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Información del Cliente
                </Typography>
              </Box>
            </Grid>

            <Grid item size={6} mt={-2}>
              <Typography variant="body1" fontWeight={600} ml={1}>Identificación del Cliente</Typography>
              <TextField
                fullWidth
                placeholder="Identificación del Cliente"
                value={formData.prestamo?JSON.parse(formData.prestamo.cliente_campos)["2"] : ""}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>

            <Grid item size={6} mt={-2}>
              <Typography variant="body1" fontWeight={600} ml={1}>Nombre del Cliente</Typography>
              <TextField
                fullWidth
                placeholder="Nombre del Cliente"
                value={formData.prestamo?.cliente_nombre || ""}
                InputProps={{ readOnly: true }}
                disabled
              />
            </Grid>


            {/* Recuperación Section */}
            <Grid item size={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AssignmentIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  Detalles de la Recuperación
                </Typography>
              </Box>
            </Grid>

            <Grid item size={6}>
              <FormControl fullWidth error={!!errors.motivo} required>
                <Typography variant="body1" fontWeight={600} ml={1}>Motivo de Recuperación</Typography>
                <Select
                  sx={{backgroundColor: 'white'}}
                  value={formData.motivo}
                  error={!!errors.motivo}
                  helperText={errors.motivo}
                  required
                  onChange={(e) => setFormData(prev => ({ ...prev, motivo: e.target.value }))}
                  placeholder="Motivo de Recuperación"
                >
                  {RECOVERY_REASONS.map((reason) => (
                    <MenuItem key={reason.id} value={reason.id}>
                      {reason.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography fontSize={12} color="error" ml={1}>{errors.motivo}</Typography>
            </Grid>

            <Grid item size={6}>
              <FormControl fullWidth>
                <Typography variant="body1" fontWeight={600} ml={1}>Prioridad</Typography>
                <Select
                  sx={{backgroundColor: 'white'}}
                  value={formData.prioridad}
                  onChange={(e) => setFormData(prev => ({ ...prev, prioridad: e.target.value }))}
                  placeholder="Prioridad"
                >
                  {RECOVERY_PRIORITIES.map((priority) => (
                    <MenuItem key={priority.id} value={priority.id}>
                      <Chip 
                        label={priority.name} 
                        color={priority.color} 
                        size="small" 
                        sx={{ mr: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item size={12}>
              <Typography variant="body1" fontWeight={600} ml={1}>Descripción del Motivo</Typography>
              <TextField
                fullWidth
                placeholder="Descripción del Motivo"
                value={formData.descripcion}
                onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              />
            </Grid>

            <Grid item size={6}>
              <Typography variant="body1" fontWeight={600} ml={1}>Ubicación Actual del Producto</Typography>
              <TextField
                fullWidth
                placeholder="Ubicación Actual del Producto"
                value={formData.ubicacion_actual}
                onChange={(e) => setFormData(prev => ({ ...prev, ubicacion_actual: e.target.value }))}
              />
            </Grid>

            <Grid item size={6}>
              <Typography variant="body1" fontWeight={600} ml={1}>Contacto Alternativo</Typography>
              <TextField
                fullWidth
                value={formData.contacto_alternativo}
                onChange={(e) => setFormData(prev => ({ ...prev, contacto_alternativo: e.target.value }))}
                placeholder="Teléfono, email, etc."
              />
            </Grid>

            <Grid item size={12}>
              <Typography variant="body1" fontWeight={600} ml={1}>Observaciones Adicionales</Typography>
              <TextField
                fullWidth
                value={formData.observaciones}
                onChange={(e) => setFormData(prev => ({ ...prev, observaciones: e.target.value }))}
                placeholder="Información adicional relevante para la recuperación"
              />
            </Grid>

            <Grid item size={6} >
              <Typography variant="body1" fontWeight={600} ml={1}>Monto de la Recuperación</Typography>
              <TextField
                fullWidth
                placeholder="Monto de la Recuperación"
                error={!!errors.monto_recuperacion}
                helperText={errors.monto_recuperacion}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      RD$
                    </InputAdornment>
                  ),
                  inputMode: "numeric",
                  pattern: "[0-9]*"
                }}
                value={formatNumberWithCommas(formData.monto_recuperacion)}
                onChange={(e) => setFormData(prev => ({ ...prev, monto_recuperacion: e.target.value }))}
              />
            </Grid>

          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleClose} 
            color="inherit"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primarybutton"
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            disabled={loading}
            sx={{ fontWeight: 'bold' }}
          >
            {loading ? 'Creando...' : 'Crear Solicitud de Recuperación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Message Dialog */}
      <AlertMessageDialog
        open={alertOpen}
        setOpen={setAlertOpen}
        handleClose={handleAlertClose}
        title={alertTitle}
        message={alertMessage}
        severity={alertSeverity}
      />
    </>
  );
}
